import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import config from '../config';
import NotificationBell from './NotificationBell';
import LocationSwitcher from './LocationSwitcher';
import ChatBubble from './ChatBubble';
import { useLocationContext } from '../contexts/LocationContext';
import { useAuth } from '../contexts/AuthContext';
import { trackSessionStart, trackPageView, track } from '../services/analyticsService';

const ALL_NAV_ITEMS = [
  { path: '/', label: 'Dashboard', key: 'dashboard' },
  { path: '/pl', label: 'P&L', key: 'pl' },
  { path: '/inventory', label: 'Inventory', key: 'inventory' },
  { path: '/maintenance', label: 'Maintenance', key: 'maintenance' },
  { path: '/timesheet', label: 'Timesheet', key: 'timesheet' },
];

const Layout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { canViewPage, isAdmin, user } = useAuth();
  const { currentLocation } = useLocationContext();
  const [useDefaultLogo, setUseDefaultLogo] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const dropdownRef = useRef(null);

  const NAV_ITEMS = ALL_NAV_ITEMS.filter(item => canViewPage(item.key));

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const currentSection = NAV_ITEMS.find(item => isActive(item.path)) || NAV_ITEMS[0];

  // Analytics: session start + heartbeat
  useEffect(() => {
    trackSessionStart();
    const heartbeat = setInterval(() => {
      if (document.visibilityState === 'visible') track('session_heartbeat');
    }, 60000);
    return () => clearInterval(heartbeat);
  }, []);

  // Analytics: page view tracking
  useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setMobileNavOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Navigation */}
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Top row: logo + right-side icons */}
          <div className="flex justify-between h-14 sm:h-16">
            <div className="flex items-center">
              <Link to="/" className="flex items-center">
                <img
                  src={useDefaultLogo ? '/logo.png' : `${config.API_BASE_URL}/settings/logo`}
                  onError={() => setUseDefaultLogo(true)}
                  alt="Logo"
                  className="h-8 sm:h-10"
                />
              </Link>
              {/* Desktop tabs */}
              <div className="hidden sm:ml-8 sm:flex sm:space-x-8">
                {NAV_ITEMS.map(item => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${
                      isActive(item.path)
                        ? 'border-blue-500 text-gray-900'
                        : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                    }`}
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>
            <div className="flex items-center space-x-1 sm:space-x-2">
              <div className="hidden sm:block">
                <LocationSwitcher />
              </div>
              <NotificationBell />
              {user?.is_superadmin && <Link
                to="/admin"
                className="p-2 text-gray-500 hover:text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                title="Admin"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </Link>}
              {isAdmin && <Link
                to="/settings"
                className="p-2 text-gray-500 hover:text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                title="Settings"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </Link>}
            </div>
          </div>

          {/* Mobile second row: section dropdown + location switcher */}
          <div className="sm:hidden pb-2 flex items-center gap-2" ref={dropdownRef}>
            <div className="relative flex-1">
              <button
                onClick={() => setMobileNavOpen(!mobileNavOpen)}
                className="flex items-center justify-between w-full px-3 py-2 text-sm font-medium text-gray-900 bg-gray-50 border border-gray-300 rounded-md"
              >
                <span>{currentSection.label}</span>
                <svg
                  className={`w-4 h-4 ml-2 transition-transform ${mobileNavOpen ? 'rotate-180' : ''}`}
                  fill="none" stroke="currentColor" viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {mobileNavOpen && (
                <div className="absolute left-0 right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-50">
                  {NAV_ITEMS.map(item => (
                    <button
                      key={item.path}
                      onClick={() => {
                        navigate(item.path);
                        setMobileNavOpen(false);
                      }}
                      className={`block w-full text-left px-4 py-3 text-sm ${
                        isActive(item.path)
                          ? 'bg-blue-50 text-blue-700 font-medium'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <LocationSwitcher />
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="py-6 sm:py-10 overflow-x-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {currentLocation?.subscribed === false && !location.pathname.startsWith('/settings') ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Location Not Subscribed</h2>
              <p className="text-gray-500 mb-4">
                This location is not included in your subscription.
                {isAdmin && ' You can enable it in Settings under Location Subscriptions.'}
              </p>
              {isAdmin && (
                <Link to="/settings" className="text-blue-600 hover:text-blue-800 font-medium">
                  Go to Settings
                </Link>
              )}
            </div>
          ) : (
            children
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-auto">
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
          <p className="text-center text-sm text-gray-500">
            Car Wash Copilot
          </p>
          <p className="text-center text-sm mt-1 space-x-3">
            <Link to="/contact" className="text-blue-600 hover:text-blue-800">
              Contact Us
            </Link>
            <Link to="/security" className="text-blue-600 hover:text-blue-800">
              Security
            </Link>
          </p>
        </div>
      </footer>
      <ChatBubble />
    </div>
  );
};

export default Layout;
